import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EllipsisVerticalIcon } from 'lucide-react';
import ArrowDownIcon from '@/shared/assets/arrow-down-icon.svg?react';
import ArrowTopRightIcon from '@/shared/assets/arrow-top-right-icon.svg?react';
import ExchangeProcessIcon from '@/shared/assets/exchange-process-icon.svg?react';
import ArrowsDownUpIcon from '@/shared/assets/arrows-down-up-icon.svg?react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/components';
import { ActionMenuItem } from '@/widgets/account-card';
import { cn } from '@/shared/utils';
import type { WalletAssetActionsVisibility, WalletAssetRow } from '../model';

const TRIGGER_CLASSNAME =
  'cursor-pointer p-0 relative shrink-0 w-[3.2rem] h-[3.2rem] rounded-[1rem] border border-stroke bg-background dark:bg-background hover:bg-secondary-bg dark:hover:bg-secondary-bg transition-colors flex items-center justify-center text-primary';

type Props = {
  row: WalletAssetRow;
  visibility: WalletAssetActionsVisibility;
  contentClassName?: string;
  triggerClassName?: string;
  onDeposit: (row: WalletAssetRow) => void;
  onWithdraw: (row: WalletAssetRow) => void;
  onExchange: (row: WalletAssetRow) => void;
  onTransfer: (row: WalletAssetRow) => void;
};

const WalletsAssetActionMenu = ({
  row,
  visibility,
  contentClassName,
  triggerClassName,
  onDeposit,
  onWithdraw,
  onExchange,
  onTransfer,
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const closeAnd = (fn: () => void) => () => {
    fn();
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        withChevron={false}
        aria-label={t('personalAccount.wallets.columns.actions')}
        className={cn(
          TRIGGER_CLASSNAME,
          open && 'bg-secondary-bg',
          triggerClassName
        )}>
        <EllipsisVerticalIcon className="size-[1.6rem]" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className={cn(
          'w-[23.9rem] min-w-[23.9rem] p-0 rounded-[1.6rem]',
          contentClassName
        )}
        classNameViewport="w-full max-w-full p-[0.8rem]">
        {visibility.deposit && (
          <ActionMenuItem
            icon={ArrowDownIcon}
            label={t('personalAccount.overview.actions.deposit')}
            onClick={closeAnd(() => onDeposit(row))}
          />
        )}
        {visibility.exchange && (
          <ActionMenuItem
            icon={ExchangeProcessIcon}
            label={t('personalAccount.overview.actions.exchange')}
            onClick={closeAnd(() => onExchange(row))}
          />
        )}
        {visibility.withdraw && (
          <ActionMenuItem
            icon={ArrowTopRightIcon}
            label={t('personalAccount.overview.actions.withdraw')}
            onClick={closeAnd(() => onWithdraw(row))}
          />
        )}
        {visibility.transfer && (
          <ActionMenuItem
            icon={ArrowsDownUpIcon}
            label={t('personalAccount.overview.actions.transfer')}
            onClick={closeAnd(() => onTransfer(row))}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { WalletsAssetActionMenu };
